/**
 * DDI portfolio slide - row assembly and the header arithmetic.
 *
 * The DDI slide is the shorter, portfolio-level read of the same monthly
 * status reports that drive the per-project slide. Nothing here fetches or
 * derives new facts: every figure is a projection of reports already filed,
 * which is what makes the slide reproducible for any past month.
 *
 * Kept pure and separate from the component so the maths behind "On track
 * 67%" and "At risk +1" is testable without rendering anything.
 */

import type { DatePrecision, Portfolio, Project, ProjectReport, ProjectStage, RagStatus } from '../types'

/**
 * How a row reads on the slide. Distinct from RagStatus because the DDI
 * audience has a fourth state: a project in its final stage is reported as
 * Closed regardless of the RAG that got it there.
 */
export type DdiStatus = 'on_track' | 'at_risk' | 'roadblocks' | 'closed'

export interface DdiRow {
  projectId: string
  name: string
  stage?: ProjectStage
  /** ISO date behind the Biz Go Live column. */
  goLive?: string
  /** How precisely the PM wants that date stated: a full month, a quarter,
   *  or not at all yet. From the report, since it is a reporting choice. */
  goLivePrecision?: DatePrecision
  /** False when the project is flagged for DDI but filed no report this month. */
  hasReport: boolean
  overall?: RagStatus
  previous?: RagStatus
  /** Null only when nothing was filed - "no status" is not a status. */
  status: DdiStatus | null
  prevStatus: DdiStatus | null
  summary: string
  /** The way forward. Stored as ddiNextSteps, the field it replaced on the slide. */
  nextSteps: string
  /** The portfolio leading this initiative - the Settings portfolio holding
   *  its sub-portfolio, or the sub-portfolio itself when none does. */
  portfolio: string
  /** Budget-year capex and opex in euros. Both zero means not yet priced. */
  capex: number
  opex: number
}

const RAG_RANK: Record<RagStatus, number> = { green: 0, amber: 1, red: 2 }

const RAG_TO_DDI: Record<RagStatus, DdiStatus> = {
  green: 'on_track',
  amber: 'at_risk',
  red: 'roadblocks',
}

/**
 * How a project reads on the DDI slide.
 *
 * The final stage wins over the RAG: a project in Realise Value & Learn has
 * landed, and reporting it as At Risk would misdescribe work that is done.
 * A project that filed nothing has no status at all - `null` rather than a
 * cheerful green, so an unfiled row can never flatter the percentages.
 */
export function ddiStatus(
  overall: RagStatus | undefined,
  stage: ProjectStage | undefined,
): DdiStatus | null {
  if (!overall) return null
  return stage === 'realise' ? 'closed' : RAG_TO_DDI[overall]
}

/** Direction of travel between two months, for the arrow between Prev and Curr. */
export function ddiDirection(
  prev: RagStatus | undefined,
  curr: RagStatus | undefined,
): 'improved' | 'worsened' | 'same' | null {
  if (!prev || !curr) return null
  if (RAG_RANK[curr] < RAG_RANK[prev]) return 'improved'
  if (RAG_RANK[curr] > RAG_RANK[prev]) return 'worsened'
  return 'same'
}

/**
 * The "Of N active" split behind the slide's header band.
 *
 * Only rows carrying an active status count. Closed work is excluded because
 * it is no longer being delivered, and unfiled rows because their position is
 * unknown - counting either would quietly move a number leadership reads as a
 * delivery health figure.
 *
 * Counts, not percentages, for three reasons: they can be reconciled against
 * the chips in the table below, they carry their own denominator, and three
 * shares of a small portfolio round badly (33/33/33 sums to 99). The one
 * percentage kept is on-track, which is the figure leadership quotes.
 *
 * At risk and roadblocks are counted separately. Folding roadblocks into
 * "at risk" would give that phrase a second meaning on a slide whose legend
 * and month-over-month deltas already treat the two as distinct states.
 */
export interface DdiShare {
  active: number
  onTrack: number
  atRisk: number
  roadblocks: number
  onTrackPct: number
}

export function ddiShare(rows: DdiRow[]): DdiShare {
  const count = (value: DdiStatus) => rows.filter(r => r.status === value).length
  const onTrack = count('on_track')
  const atRisk = count('at_risk')
  const roadblocks = count('roadblocks')
  const active = onTrack + atRisk + roadblocks
  return {
    active,
    onTrack,
    atRisk,
    roadblocks,
    onTrackPct: active === 0 ? 0 : Math.round((onTrack / active) * 100),
  }
}

/**
 * Month-over-month movement in the two counts leadership tracks.
 *
 * A project appearing for the first time counts on the current side only, and
 * one that stopped filing counts on the previous side only, so both show as a
 * real move rather than being silently dropped.
 */
export function ddiDeltas(rows: DdiRow[]): { atRisk: number; roadblocks: number } {
  const count = (key: 'status' | 'prevStatus', value: DdiStatus) =>
    rows.filter(r => r[key] === value).length
  return {
    atRisk: count('status', 'at_risk') - count('prevStatus', 'at_risk'),
    roadblocks: count('status', 'roadblocks') - count('prevStatus', 'roadblocks'),
  }
}

/**
 * The month-over-month movement as a sentence: "1 fewer at risk, 1 more
 * roadblock since last month". Words rather than signed numbers, because a
 * "-1" on an orange chip reads as bad news when it is the opposite, and
 * because a sentence lets each state be named once on the slide.
 *
 * `tone` carries the verdict for the arrow's colour: any increase in a bad
 * state is worse, otherwise any decrease is better.
 */
export interface DdiMovement {
  text: string
  tone: 'better' | 'worse' | 'same'
}

export function ddiMovement(deltas: { atRisk: number; roadblocks: number }): DdiMovement {
  const parts: string[] = []
  const describe = (n: number, singular: string, plural: string) => {
    const word = Math.abs(n) === 1 ? singular : plural
    return `${Math.abs(n)} ${n > 0 ? 'more' : 'fewer'} ${word}`
  }
  if (deltas.atRisk !== 0) parts.push(describe(deltas.atRisk, 'at risk', 'at risk'))
  if (deltas.roadblocks !== 0) parts.push(describe(deltas.roadblocks, 'roadblock', 'roadblocks'))
  if (parts.length === 0) return { text: 'No change since last month', tone: 'same' }
  const worse = deltas.atRisk > 0 || deltas.roadblocks > 0
  return { text: `${parts.join(', ')} since last month`, tone: worse ? 'worse' : 'better' }
}

/**
 * ISO date to the slide's MM/YY. Parsed by slicing rather than via Date so a
 * date-only string cannot shift a month across a timezone boundary.
 */
export function formatGoLive(iso?: string, precision: DatePrecision = 'month'): string {
  if (!iso || precision === 'tbc') return 'TBC'
  const m = /^(\d{4})-(\d{2})/.exec(iso)
  if (!m) return 'TBC'
  const yy = m[1].slice(2)
  if (precision === 'quarter') return `Q${Math.ceil(Number(m[2]) / 3)}/${yy}`
  return `${m[2]}/${yy}`
}

/**
 * Euros for the Budget column, in whichever unit reads at a glance: "80k",
 * "450k", "1.8Mn". A fixed EUR millions column showed the real portfolio as
 * a row of 0.0 and 0.1. Zero and absent both read TBC - an unfunded
 * programme and an unpriced one are both "not yet a number".
 */
export function formatBudget(value?: number): string {
  if (!value) return 'TBC'
  if (value >= 1_000_000) {
    const mn = value / 1_000_000
    return `${Number.isInteger(mn) ? mn : mn.toFixed(1)}Mn`
  }
  if (value >= 1_000) return `${Math.round(value / 1_000)}k`
  return String(value)
}

/**
 * The portfolio a sub-portfolio rolls up to, from the groupings in Settings.
 * A sub-portfolio nobody has grouped yet stands for itself rather than landing
 * in an "Other" bucket, so a half-configured Settings still reads sensibly.
 */
export function portfolioOf(subPortfolio: string | undefined, portfolios: Portfolio[] = []): string {
  if (!subPortfolio) return 'Unassigned'
  return portfolios.find(p => p.subPortfolios.includes(subPortfolio))?.name ?? subPortfolio
}

export interface DdiGroup {
  portfolio: string
  rows: DdiRow[]
  capex: number
  opex: number
}

/**
 * Rows grouped by the portfolio leading them, for the slide's portfolio rail.
 *
 * Groups follow the order portfolios are listed in Settings, then any
 * ungrouped sub-portfolios alphabetically. Rows keep the order they arrive
 * in, so the active-closed-unreported banding holds inside every group.
 */
export function ddiGroups(rows: DdiRow[], order: string[] = []): DdiGroup[] {
  const groups = new Map<string, DdiGroup>()
  for (const row of rows) {
    const g = groups.get(row.portfolio) ?? { portfolio: row.portfolio, rows: [], capex: 0, opex: 0 }
    g.rows.push(row)
    g.capex += row.capex
    g.opex += row.opex
    groups.set(row.portfolio, g)
  }
  const rank = (name: string) => {
    const i = order.indexOf(name)
    return i === -1 ? order.length : i
  }
  return [...groups.values()].sort((a, b) => rank(a.portfolio) - rank(b.portfolio) || a.portfolio.localeCompare(b.portfolio))
}

export function stageCounts(rows: DdiRow[]): Record<ProjectStage, number> {
  const counts: Record<ProjectStage, number> = {
    discover: 0, design: 0, develop: 0, scale: 0, realise: 0,
  }
  for (const row of rows) if (row.stage) counts[row.stage] += 1
  return counts
}

/**
 * Assemble one row per DDI-flagged project from the month's reports.
 *
 * Snapshot fields win over live project fields wherever a report exists, so a
 * past month renders the name, stage, go-live, sub-portfolio and capex/opex
 * split it was signed off with. The portfolio grouping is the exception: it
 * comes from Settings as they are now, because it describes who leads the
 * work today rather than a fact frozen at filing.
 *
 * Sorted in three bands: active work first (alphabetically), then closed, then
 * anything that has not reported. The bottom of the slide is where a reader
 * expects the things that need chasing rather than reading.
 */
export function buildDdiRows(
  projects: Project[],
  reports: ProjectReport[],
  prevReports: ProjectReport[],
  portfolios: Portfolio[] = [],
): DdiRow[] {
  const budgetYear = String(new Date().getFullYear())
  const byProject = new Map(reports.map(r => [r.projectId, r]))
  const prevByProject = new Map(prevReports.map(r => [r.projectId, r]))

  const rows: DdiRow[] = projects
    .filter(p => p.ddi)
    .map(project => {
      const report = byProject.get(project.id)
      const prev = prevByProject.get(project.id)
      const stage = (report?.snapshot.stage as ProjectStage | undefined) ?? project.stage
      const prevStage = (prev?.snapshot.stage as ProjectStage | undefined) ?? stage
      return {
        projectId: project.id,
        name: report?.snapshot.projectName ?? project.name,
        stage,
        goLive: report?.snapshot.goLive ?? project.actualGoLive ?? project.dueDate,
        goLivePrecision: report?.datePrecision ?? 'month',
        hasReport: !!report,
        overall: report?.overall,
        previous: prev?.overall,
        status: ddiStatus(report?.overall, stage),
        prevStatus: ddiStatus(prev?.overall, prevStage),
        summary: report?.ddiSummary ?? '',
        nextSteps: report?.ddiNextSteps ?? '',
        portfolio: portfolioOf(report?.snapshot.subPortfolio ?? project.subPortfolio, portfolios),
        // Unfiled rows have no snapshot, so they fall back to this year's
        // budget on the project itself.
        capex: report ? report.snapshot.capex ?? 0 : project.budgetByYear?.[budgetYear]?.capex ?? 0,
        opex: report ? report.snapshot.opex ?? 0 : project.budgetByYear?.[budgetYear]?.opex ?? 0,
      }
    })

  const band = (row: DdiRow) => (!row.hasReport ? 2 : row.status === 'closed' ? 1 : 0)
  return rows.sort((a, b) => band(a) - band(b) || a.name.localeCompare(b.name))
}

// Most rows the slide carries. Text is always shown in full and the type
// steps down to fit, so this cap is about legibility rather than space.
export const DDI_COMPACT_MAX = 15

export interface DdiLayout {
  visible: number
  hidden: number
}

/**
 * How many rows fit, and how many had to be dropped.
 *
 * Only the count: the slide decides the type size by measuring what fits.
 *
 * Past the compact limit the last slot is spent on the "+N not shown" note, so
 * the slide states what it dropped instead of simply ending early.
 */
export function ddiLayout(rowCount: number): DdiLayout {
  if (rowCount <= DDI_COMPACT_MAX) return { visible: rowCount, hidden: 0 }
  const visible = DDI_COMPACT_MAX - 1
  return { visible, hidden: rowCount - visible }
}
